import React, { useState, useCallback, useRef, useEffect } from 'react';
import { TablaMoiEscalerasVista } from '../../vista/tablaMoiEscalerasVista';
import { useCargaMasivaMatriz } from '../useCargaMasivaMatriz';
import { mensajeBorradoFilaMatriz } from '../usoMatriz';
import type { IConfiguracionMoiEscalera } from '../../interfaces';
import { MoiEscalerasInicial } from '../../interfaces';
import { useNotificaciones } from '../../../../globales/contextos/notificationContext';
import { useAvisoErrorConsulta } from '../useAvisoErrorConsulta';
import { EnumTipoNotificacion } from '../../../../globales/interfaces';
import { CotizadorModelo } from '../../../../modelos/CotizadorNBS';
import { useConfirmacion } from '../../../../globales/contextos/confirmacionContext';
import { useCargando } from '../../../../globales/contextos/cargandoContext';

interface MoiEscalerasCVProps {
  datosIniciales?: IConfiguracionMoiEscalera[];
  onRegistrarCancelacion?: (key: string, canceler: () => void) => void;
  onIniciarEdicionGeneral?: () => void;
}
import { usePaisConfiguracion } from '../paisConfiguracion';
import { esFilaNueva, idFilaNueva } from '../filaNuevaMatriz';
import { useMonedaPais } from '../useMonedaPais';

export const MoiEscalerasCV: React.FC<MoiEscalerasCVProps> = ({
  datosIniciales = MoiEscalerasInicial,
  onRegistrarCancelacion,
  onIniciarEdicionGeneral
}) => {
  const [moiEscaleras, setMoiEscaleras] = useState<IConfiguracionMoiEscalera[]>(datosIniciales);
  const [filaEnEdicion, setFilaEnEdicion] = useState<string | null>(null);
  const [respaldoFila, setRespaldoFila] = useState<any>(null);
  const [intentoGuardar, setIntentoGuardar] = useState(false);
  const { mostrarNotificacion } = useNotificaciones();
  const { confirmar } = useConfirmacion();
  const { manejarCargando } = useCargando();
  const paisId = usePaisConfiguracion();
  const avisarErrorConsulta = useAvisoErrorConsulta();
  const { monedaLocal } = useMonedaPais(paisId);
  // Se incrementa tras una carga masiva para volver a consultar la matriz.
  const [versionDatos, setVersionDatos] = useState(0);

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const modelo = new CotizadorModelo();
        const res = await modelo.obtenerMatrices<IConfiguracionMoiEscalera>('moi-escalera', paisId as number);
        if (res?.Data) {
          setMoiEscaleras(res.Data.map((item: any) => ({ ...item, Id: String(item.Id) })));
        }
      } catch (error) {
        avisarErrorConsulta(error, 'la matriz M.O.D de escaleras');
      }
    };
    cargarDatos();
  }, [paisId, versionDatos]);

  const mostrarNotificacionObligatorio = useCallback(() => {
    setIntentoGuardar(true);
    mostrarNotificacion(
      EnumTipoNotificacion.Warning,
      'Campos Obligatorios',
      'Debes diligenciar todos los campos obligatorios antes de guardar la fila.'
    );
  }, [mostrarNotificacion]);

  const respaldoFilaRef = useRef(respaldoFila);
  useEffect(() => {
    respaldoFilaRef.current = respaldoFila;
  }, [respaldoFila]);

  const cancelarEdicion = useCallback(() => {
    setIntentoGuardar(false);
    setFilaEnEdicion(null);
    const respaldo = respaldoFilaRef.current;
    if (respaldo) {
      if (respaldo.__esNueva || esFilaNueva(respaldo.Id)) {
        setMoiEscaleras(prev => prev.filter(f => f.Id !== respaldo.Id));
      } else {
        const { __esNueva, ...limpio } = respaldo;
        setMoiEscaleras(prev => prev.map(f => f.Id === limpio.Id ? limpio : f));
      }
      setRespaldoFila(null);
    }
  }, []);

  useEffect(() => {
    onRegistrarCancelacion?.('moiEscalera', cancelarEdicion);
  }, [onRegistrarCancelacion, cancelarEdicion]);

  const actualizarFila = useCallback((id: string, campo: string, valor: string | number) => {
    setMoiEscaleras(prev => prev.map(fila => fila.Id === id ? { ...fila, [campo]: valor } : fila));
  }, []);

  const eliminarFila = useCallback(async (id: string) => {
    // Cotizaciones abiertas cuya escalera cae hoy en esta fila: se avisa antes de borrar.
    let mensaje = '¿Está seguro de que desea eliminar esta configuración? Esta acción no se puede deshacer.';
    if (!esFilaNueva(id)) {
      // Cargando mientras se consulta qué cotizaciones usan la fila.
      manejarCargando(true, undefined, true);
      try {
        const uso = (await new CotizadorModelo().obtenerUsoFilaMatriz('moi-escalera', Number(id))).Data ?? [];
        mensaje = mensajeBorradoFilaMatriz(uso,
          'Si la eliminas, al recalcularlas tomarán los días y el costo de la siguiente altura mayor (o quedarán sin valor si no hay una mayor).', mensaje);
      } catch (error) {
        console.error('No se pudo consultar el uso de la fila', error);
      } finally {
        manejarCargando(false);
      }
    }
    const confirmado = await confirmar({
      titulo: 'Eliminar fila',
      mensaje,
      textoAceptar: 'Sí, eliminar',
      tipo: EnumTipoNotificacion.Warning,
      opcionCancelar: true
    });

    if (confirmado) {
      try {
        const isNew = esFilaNueva(id);
        if (!isNew) {
          const modelo = new CotizadorModelo();
          await modelo.eliminarMatrizFila('moi-escalera', Number(id));
        }
        setMoiEscaleras(prev => prev.filter(fila => fila.Id !== id));
        mostrarNotificacion(EnumTipoNotificacion.Success, 'Fila Eliminada', 'La configuración se eliminó correctamente.');
      } catch (error) {
        console.error(error);
        mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', 'Ocurrió un error al eliminar la fila.');
      }
    }
  }, [mostrarNotificacion, confirmar, manejarCargando]);

  const agregarFila = useCallback(() => {
    setIntentoGuardar(false);
    onIniciarEdicionGeneral?.();
    const nuevaFila: IConfiguracionMoiEscalera = {
      Id: idFilaNueva(),
      Altura: "", Costo: "" as any, Tiempo: "" as any
    };
    setMoiEscaleras(prev => [nuevaFila, ...prev]);
    setRespaldoFila({ ...nuevaFila, __esNueva: true });
    setFilaEnEdicion(nuevaFila.Id);
  }, [onIniciarEdicionGeneral]);

  const guardarFila = useCallback(async (id: string) => {
    const filaToSave = moiEscaleras.find(f => f.Id === id);
    if (!filaToSave) return;
    
    try {
      const modelo = new CotizadorModelo();
      const isNew = esFilaNueva(id);
      const dbId = isNew ? null : Number(id);

      const payload = {
        PaisId: paisId,
        Altura: Number(filaToSave.Altura),
        Costo: Number(filaToSave.Costo),
        Tiempo: Number(filaToSave.Tiempo)
      };

      const res = await modelo.guardarMatrizFila('moi-escalera', dbId, payload);

      if (res?.NotificationType === 1) { // Success
        if (isNew && res.Data) {
          setMoiEscaleras(prev => prev.map(f => f.Id === id ? { ...f, Id: String(res.Data) } : f));
        }
        setIntentoGuardar(false);
        setFilaEnEdicion(null);
        setRespaldoFila(null);
        mostrarNotificacion(EnumTipoNotificacion.Success, 'Fila Guardada', 'La configuración se guardó correctamente.');
      } else {
        mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', res?.Messages?.[0] || 'No se pudo guardar la fila.');
      }
    } catch (error: any) {
      console.error(error);
      mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', error?.mensajeBackend || 'Ocurrió un error inesperado al guardar la fila.');
    }
  }, [moiEscaleras, mostrarNotificacion]);

  const iniciarEdicion = useCallback((fila: any) => {
    setIntentoGuardar(false);
    onIniciarEdicionGeneral?.();
    setRespaldoFila({ ...fila });
    setFilaEnEdicion(fila.Id);
  }, [onIniciarEdicionGeneral]);

  const { descargarPlantilla, cargaMasiva, modalResultado } = useCargaMasivaMatriz({
    matriz: 'moi-escalera',
    etiquetaReferencia: 'Altura',
    antesDeCargar: () => { if (filaEnEdicion) cancelarEdicion(); },
    alTerminar: () => setVersionDatos(v => v + 1)
  });

  return (
    <>
    {modalResultado}
    <TablaMoiEscalerasVista
      alRecargar={() => { cancelarEdicion(); setVersionDatos(v => v + 1); }}
      alDescargarPlantilla={descargarPlantilla}
      alCargaMasiva={cargaMasiva}
      datos={moiEscaleras}
      actualizarFila={actualizarFila}
      eliminarFila={eliminarFila}
      guardarFila={guardarFila}
      filaEnEdicion={filaEnEdicion}
      iniciarEdicion={iniciarEdicion}
      cancelarEdicion={cancelarEdicion}
      agregarFila={agregarFila}
      mostrarNotificacionObligatorio={mostrarNotificacionObligatorio}
      intentoGuardar={intentoGuardar}
      monedaLocal={monedaLocal}
    />
    </>
  );
};
